import { toStatus } from '@/features/cases/api/caseWire'
import type {
  Client,
  ClientManager,
  ClientMoneySummary,
  ClientOverviewRow,
  ClientStatement,
  ClientsOverview,
  Money,
  MoneyUnavailable,
  PaymentMethod,
  StatementRow,
  StatementRowMoney,
  YearFilter,
} from '@/types/domain'

/* The seam between the server's spelling of the clients reads and the client's
 * (cmd/asa/readmodel_clients.go). Two things happen here and only here: JSON
 * nulls — and the empty string the server sends for a name it could not
 * resolve — become absent fields, because every screen tests presence rather
 * than null; and a statement row's case status is folded through the same
 * `toStatus` the case list uses, so the pill vocabulary has one owner.
 *
 * And one default: `refunded` (S-22f) is served on every money summary by a
 * server that knows refunds; one deployed before them sends none, which is "no
 * refund" — a zero, so the «Возврат» column never prints a dash for a figure
 * that is simply nil. */

/** A money summary as served: `refunded` may be missing from an older server. */
export type ClientMoneySummaryWire = Omit<ClientMoneySummary, 'refunded'> & {
  refunded?: Money
}

export interface ClientOverviewRowWire {
  id: string
  name: string
  code: string
  taxId: string
  country: string
  isActive: boolean
  number: string
  ownerId: string
  /** "" when the server could not resolve the name. */
  ownerName: string
  createdById: string
  createdByName: string
  managers: ClientManager[]
  cases: number
  weightKg: string | null
  money?: ClientMoneySummaryWire
}

export interface ClientsOverviewWire {
  year: number | null
  years: number[]
  items: ClientOverviewRowWire[]
  totals?: ClientMoneySummaryWire
  moneyUnavailable?: MoneyUnavailable
}

export interface StatementRowWire {
  caseId: string
  number: string
  status: string
  openedOn: string
  route: { loading: string; border: string; discharge: string; unloading: string }
  transportModeCode: string
  cargoDescription: string
  weightKg: string | null
  volumeM3: string | null
  paymentMethod: string
  eta: string | null
  ata: string | null
  transitDays: number | null
  ownerName: string
  money?: {
    billed: Money
    received: Money
    refunded?: Money
    balance: Money
    running: Money
    cost: Money
    lastReceivedOn: string | null
  }
}

export interface ClientStatementWire {
  client: Client
  /** "" when the server could not resolve the name. */
  ownerName: string
  createdByName: string
  year: number | null
  years: number[]
  opening?: { balance: Money }
  rows: StatementRowWire[]
  totals?: ClientMoneySummaryWire & { weightKg: string | null }
  moneyUnavailable?: MoneyUnavailable
}

const orAbsent = <T>(value: T | null | undefined): T | undefined =>
  value === null || value === undefined ? undefined : value

const USD_ZERO: Money = { amount: '0.00', currency: 'USD' }

function toMoneySummary(wire: ClientMoneySummaryWire): ClientMoneySummary {
  return { ...wire, refunded: wire.refunded ?? USD_ZERO }
}

export function toClientsOverview(wire: ClientsOverviewWire): ClientsOverview {
  return {
    year: wire.year,
    years: wire.years,
    items: wire.items.map(toOverviewRow),
    totals: wire.totals && toMoneySummary(wire.totals),
    moneyUnavailable: wire.moneyUnavailable,
  }
}

function toOverviewRow(wire: ClientOverviewRowWire): ClientOverviewRow {
  return {
    id: wire.id,
    name: wire.name,
    code: wire.code || undefined,
    taxId: wire.taxId || undefined,
    country: wire.country,
    isActive: wire.isActive,
    number: wire.number,
    ownerId: wire.ownerId,
    ownerName: wire.ownerName || undefined,
    createdById: wire.createdById,
    createdByName: wire.createdByName || undefined,
    managers: wire.managers,
    cases: wire.cases,
    weightKg: orAbsent(wire.weightKg),
    money: wire.money && toMoneySummary(wire.money),
  }
}

export function toClientStatement(wire: ClientStatementWire): ClientStatement {
  return {
    client: wire.client,
    ownerName: wire.ownerName || undefined,
    createdByName: wire.createdByName || undefined,
    year: wire.year,
    years: wire.years,
    opening: wire.opening,
    rows: wire.rows.map(toStatementRow),
    totals: wire.totals && {
      ...toMoneySummary(wire.totals),
      weightKg: orAbsent(wire.totals.weightKg),
    },
    moneyUnavailable: wire.moneyUnavailable,
  }
}

const METHODS: PaymentMethod[] = ['cash', 'bank_transfer']

/** The payment method as the `cases.method.*` catalogue knows it.
 *
 *  A code this build has no word for is ABSENT rather than passed through:
 *  the statement would otherwise print the wire spelling (`bank_transfer`) to
 *  an accountant, which reads as data escaping rather than as a method. */
function toPaymentMethod(wire: string): PaymentMethod | undefined {
  return METHODS.find((method) => method === wire)
}

function toStatementRow(wire: StatementRowWire): StatementRow {
  const money: StatementRowMoney | undefined = wire.money && {
    billed: wire.money.billed,
    received: wire.money.received,
    refunded: wire.money.refunded ?? USD_ZERO,
    balance: wire.money.balance,
    running: wire.money.running,
    cost: wire.money.cost,
    lastReceivedOn: orAbsent(wire.money.lastReceivedOn),
  }
  return {
    caseId: wire.caseId,
    number: wire.number,
    status: toStatus(wire.status),
    openedOn: wire.openedOn,
    route: wire.route,
    transportModeCode: wire.transportModeCode,
    cargoDescription: wire.cargoDescription,
    weightKg: orAbsent(wire.weightKg),
    volumeM3: orAbsent(wire.volumeM3),
    paymentMethod: toPaymentMethod(wire.paymentMethod),
    eta: orAbsent(wire.eta),
    ata: orAbsent(wire.ata),
    transitDays: orAbsent(wire.transitDays),
    ownerName: wire.ownerName,
    money,
  }
}

/** `?year=` as the server reads it: absent means every year. */
export function yearParam(year: YearFilter): string | undefined {
  return year === 'all' ? undefined : String(year)
}
